import { useCallback, useEffect, useMemo, useState } from "react";

import { WeeklyMetricCard } from "../components/WeeklyMetricCard";
import { ApiError, type FamilyApi } from "../lib/api";
import type { WeeklyStats } from "../lib/types";

interface StatsPageProps {
  api: FamilyApi;
  offline: boolean;
  initialWeekStart?: string;
}

export function StatsPage({ api, offline, initialWeekStart = currentMonday() }: StatsPageProps) {
  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [stats, setStats] = useState<WeeklyStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setStats(await api.getWeeklyStats(weekStart)); }
    catch (caught) { setError(caught instanceof ApiError ? caught.message : "統計暫時載入失敗，請再試一次。"); }
    finally { setLoading(false); }
  }, [api, weekStart]);

  useEffect(() => { void load(); }, [load]);

  const summary = useMemo(() => {
    if (!stats) return null;
    const metrics = stats.household ? [...stats.members, stats.household] : stats.members;
    const assigned = metrics.reduce((sum, item) => sum + item.assignedDue, 0);
    const onTime = metrics.reduce((sum, item) => sum + item.onTime, 0);
    return {
      completed: metrics.reduce((sum, item) => sum + item.completed, 0),
      points: metrics.reduce((sum, item) => sum + item.points, 0),
      overdue: metrics.reduce((sum, item) => sum + item.overdueOpen, 0),
      rate: assigned === 0 ? null : Math.round((onTime / assigned) * 100),
    };
  }, [stats]);

  return (
    <section className="page-stack" aria-labelledby="stats-title">
      <header className="page-heading"><h1 id="stats-title">本週統計</h1><p>看見完成的努力，不拿家人互相比較。</p></header>
      <div className="week-picker">
        <button type="button" aria-label="上一週" disabled={loading || offline} onClick={() => setWeekStart(addDays(weekStart, -7))}>←</button>
        <div><strong>{weekStart === currentMonday() ? "本週" : "這一週"}</strong><span>{formatWeek(weekStart)}</span></div>
        <button type="button" aria-label="下一週" disabled={loading || offline || weekStart >= currentMonday()} onClick={() => setWeekStart(addDays(weekStart, 7))}>→</button>
      </div>
      {offline && stats ? <p className="stale-note">目前顯示離線前最後載入的統計。</p> : null}
      {error ? <div className="notice notice-error" role="alert">{error}<button type="button" onClick={() => void load()}>重新整理</button></div> : null}
      {loading && !stats ? <div className="stats-skeleton" aria-label="正在載入本週統計" aria-busy="true"><span /><span /><span /></div> : null}
      {summary ? (
        <div className="summary-strip" aria-label="全家本週摘要">
          <div><span>完成</span><strong>{summary.completed}</strong><small>件</small></div>
          <div><span>準時率</span><strong>{summary.rate === null ? "—" : `${summary.rate}%`}</strong></div>
          <div><span>點數</span><strong>{summary.points}</strong><small>點</small></div>
          <div className={summary.overdue > 0 ? "metric-alert" : ""}><span>逾期</span><strong>{summary.overdue}</strong><small>件</small></div>
        </div>
      ) : null}
      {stats ? (
        <div className="weekly-grid">
          {stats.household ? <WeeklyMetricCard metric={stats.household} household /> : null}
          {[...stats.members].sort((a, b) => a.sortOrder - b.sortOrder).map((metric) => <WeeklyMetricCard key={metric.memberId} metric={metric} />)}
        </div>
      ) : null}
      <p className="stats-note">準時率只計算本週到期的待辦；未排日期的任務仍會計入完成數與點數。</p>
    </section>
  );
}

function currentMonday() {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const date = new Date(`${today}T00:00:00Z`);
  return addDays(today, -((date.getUTCDay() + 6) % 7));
}
function addDays(localDate: string, days: number) { const date = new Date(`${localDate}T00:00:00Z`); date.setUTCDate(date.getUTCDate() + days); return date.toISOString().slice(0, 10); }
function formatWeek(start: string) { const end = addDays(start, 6); const format = (value: string) => new Intl.DateTimeFormat("zh-TW", { month: "numeric", day: "numeric", timeZone: "Asia/Taipei" }).format(new Date(`${value}T12:00:00+08:00`)); return `${format(start)}－${format(end)}`; }
